import React, { useState, useEffect, memo } from 'react';
import { useAdminSmsResponses } from 'data/repositories/adminSmsResponsesRepository';
import { showToast } from 'data/reducers/toastReducer';
import { useDispatch } from 'react-redux';
import Spinner from 'view/components/Spinner';
import Paginator from 'view/components/Paginator';
import SmsResponseStatistics from './SmsResponseStatistics';
import SmsResponseFilters from './SmsResponseFilters';
import SmsResponseTable from './SmsResponseTable';
import SmsResponseDetailsModal from './SmsResponseDetailsModal';
import SmsOutboundStatistics from './SmsOutboundStatistics';
import SmsOutboundFilters from './SmsOutboundFilters';
import SmsOutboundTable from './SmsOutboundTable';
import SmsOutboundDetailsModal from './SmsOutboundDetailsModal';
import './AdminSmsResponses.css';
import AdminLayout from '../AdminLayout';
import { usePageTitle } from 'utils/pageTitle';

const ERROR_ALERT_CLASS = 'alert alert-danger admin-sms-error-alert';

/**
 * First non-empty message among the repository error keys handed in, or null
 * when none of those requests failed.
 */
const firstErrorMessage = (...messages) =>
	messages.find(
		(message) => typeof message === 'string' && message.trim().length > 0,
	) || null;

/**
 * Renders a repository failure as an alert an admin can read, so a rejected or
 * missing request is never presented as a confident zero.
 */
const renderErrorAlert = (message) =>
	message ? (
		<div className={ERROR_ALERT_CLASS} role="alert">
			{message}
		</div>
	) : null;

const AdminSmsResponses = memo(() => {
	usePageTitle('Admin | SMS Responses & Outbound');
	const dispatch = useDispatch();

	const {
		items,
		isLoading,
		pagination,
		actions,
		error,
		statistics,
		statisticsError,
		filterOptions,
		filterOptionsError,
		outboundList,
		outboundListPagination,
		outboundListError,
		isLoadingOutboundList,
		outboundStatistics,
		outboundStatisticsError,
		outboundFilterOptions,
		outboundFilterOptionsError,
	} = useAdminSmsResponses();

	// Tab state: 'inbound' | 'outbound'
	const [activeTab, setActiveTab] = useState('inbound');

	// Inbound state
	const [filters, setFilters] = useState({
		search: '',
		has_reply: 'all',
		signature_valid: 'all',
		intent: 'all',
		date_from: '',
		date_to: '',
		sort_by: 'created_at',
		sort_order: 'desc',
	});
	const [statsDays, setStatsDays] = useState(7);
	const [selectedItem, setSelectedItem] = useState(null);
	const [showDetailsModal, setShowDetailsModal] = useState(false);

	// Outbound state
	const [outboundFilters, setOutboundFilters] = useState({
		search: '',
		status: 'all',
		tier: 'all',
		template_key: 'all',
		date_from: '',
		date_to: '',
		sort_by: 'created_at',
		sort_order: 'desc',
	});
	const [outboundPage, setOutboundPage] = useState(1);
	const [outboundStatsDays, setOutboundStatsDays] = useState(7);
	const [selectedOutboundSms, setSelectedOutboundSms] = useState(null);
	const [showOutboundModal, setShowOutboundModal] = useState(false);

	// Fetch Inbound on mount and filters change
	useEffect(() => {
		if (activeTab === 'inbound') {
			actions.fetchPaginated(1, 25, filters);
		}
	}, [actions, filters, activeTab]);

	useEffect(() => {
		if (activeTab === 'inbound') {
			actions.getStatistics({ days: statsDays });
			actions.getFilterOptions();
		}
	}, [actions, statsDays, activeTab]);

	// Fetch Outbound when outbound tab active or filters change
	useEffect(() => {
		if (activeTab === 'outbound') {
			actions.getOutboundList({
				page: outboundPage,
				per_page: 25,
				...outboundFilters,
			});
		}
	}, [actions, outboundPage, outboundFilters, activeTab]);

	useEffect(() => {
		if (activeTab === 'outbound') {
			actions.getOutboundStatistics({ days: outboundStatsDays });
			actions.getOutboundFilterOptions();
		}
	}, [actions, outboundStatsDays, activeTab]);

	// Inbound Handlers
	const handleFilterChange = (key, value) => {
		setFilters((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const handleSearch = (nextFilters) => {
		setFilters((prev) => ({
			...prev,
			...nextFilters,
		}));
	};

	const handleResetFilters = () => {
		setFilters({
			search: '',
			has_reply: 'all',
			signature_valid: 'all',
			intent: 'all',
			date_from: '',
			date_to: '',
			sort_by: 'created_at',
			sort_order: 'desc',
		});
	};

	const handlePageChange = (page) => {
		actions.fetchPaginated(page, pagination.perPage, filters);
	};

	const handleSort = (column) => {
		const newOrder =
			filters.sort_by === column && filters.sort_order === 'asc'
				? 'desc'
				: 'asc';

		setFilters((prev) => ({
			...prev,
			sort_by: column,
			sort_order: newOrder,
		}));
	};

	const handleViewDetails = async (row) => {
		try {
			const response = await actions.fetchOne(row.id);
			const detail = response?.data?.data || response?.data || row;
			setSelectedItem(detail);
			setShowDetailsModal(true);
		} catch (error) {
			setSelectedItem(row);
			setShowDetailsModal(true);
			dispatch(
				showToast({
					title: 'Warning',
					body: 'Showing list data; full details could not be loaded',
					type: 'warning',
				}),
			);
		}
	};

	const handleRefreshStats = () => {
		actions.getStatistics({ days: statsDays });
	};

	// Outbound Handlers
	const handleOutboundFilterChange = (key, value) => {
		setOutboundPage(1);
		setOutboundFilters((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const handleOutboundSearch = (nextFilters) => {
		setOutboundPage(1);
		setOutboundFilters((prev) => ({
			...prev,
			...nextFilters,
		}));
	};

	const handleOutboundResetFilters = () => {
		setOutboundPage(1);
		setOutboundFilters({
			search: '',
			status: 'all',
			tier: 'all',
			template_key: 'all',
			date_from: '',
			date_to: '',
			sort_by: 'created_at',
			sort_order: 'desc',
		});
	};

	const handleOutboundPageChange = (page) => {
		setOutboundPage(page);
	};

	const handleOutboundSort = (column) => {
		const newOrder =
			outboundFilters.sort_by === column && outboundFilters.sort_order === 'asc'
				? 'desc'
				: 'asc';

		setOutboundPage(1);
		setOutboundFilters((prev) => ({
			...prev,
			sort_by: column,
			sort_order: newOrder,
		}));
	};

	const handleViewOutboundDetails = async (row) => {
		try {
			const response = await actions.fetchOutboundOne({ id: row.id });
			const detail = response?.data?.data || response?.data || row;
			setSelectedOutboundSms(detail);
			setShowOutboundModal(true);
		} catch (error) {
			setSelectedOutboundSms(row);
			setShowOutboundModal(true);
			dispatch(
				showToast({
					title: 'Warning',
					body: 'Showing list data; full details could not be loaded',
					type: 'warning',
				}),
			);
		}
	};

	const handleRefreshOutboundStats = () => {
		actions.getOutboundStatistics({ days: outboundStatsDays });
	};

	// Failures of the supporting requests. The statistics components render
	// nothing when their payload is absent, so without this the tab reports
	// zero activity for a request that never succeeded.
	const inboundContextError = firstErrorMessage(
		statisticsError,
		filterOptionsError,
	);
	const outboundContextError = firstErrorMessage(
		outboundStatisticsError,
		outboundFilterOptionsError,
	);

	const outboundItemsList = Array.isArray(outboundList) ? outboundList : [];
	const outboundPaginationData = outboundListPagination || {
		currentPage: 1,
		lastPage: 1,
		perPage: 25,
		total: outboundItemsList.length,
	};

	return (
		<AdminLayout>
			<div className="admin-sms-responses">
				<div className="page-header mb-4">
					<div>
						<h1>SMS Management</h1>
						<p className="text-muted">
							Monitor inbound client responses, RESOLVE replies, and outbound SMS alert delivery
						</p>
					</div>
				</div>

				{/* Tabs Header */}
				<div className="sms-tabs-wrapper mb-4">
					<ul className="nav nav-pills">
						<li className="nav-item">
							<button
								type="button"
								className={`nav-link ${activeTab === 'inbound' ? 'active' : ''}`}
								onClick={() => setActiveTab('inbound')}
							>
								<i className="bi bi-inbox me-2" />
								Inbound Responses
								{pagination?.total > 0 && (
									<span className={`badge ms-2 ${activeTab === 'inbound' ? 'bg-light text-dark' : 'bg-secondary'}`}>
										{pagination.total.toLocaleString()}
									</span>
								)}
							</button>
						</li>
						<li className="nav-item">
							<button
								type="button"
								className={`nav-link ${activeTab === 'outbound' ? 'active' : ''}`}
								onClick={() => setActiveTab('outbound')}
							>
								<i className="bi bi-send me-2" />
								Outbound Alerts
								{outboundPaginationData?.total > 0 && (
									<span className={`badge ms-2 ${activeTab === 'outbound' ? 'bg-light text-dark' : 'bg-secondary'}`}>
										{outboundPaginationData.total.toLocaleString()}
									</span>
								)}
							</button>
						</li>
					</ul>
				</div>

				{/* INBOUND TAB */}
				{activeTab === 'inbound' && (
					<>
						{renderErrorAlert(inboundContextError)}

						<SmsResponseStatistics
							statistics={statistics}
							isLoading={false}
							statsDays={statsDays}
							onDaysChange={setStatsDays}
							onRefresh={handleRefreshStats}
						/>

						<SmsResponseFilters
							filters={filters}
							filterOptions={filterOptions}
							onFilterChange={handleFilterChange}
							onSearch={handleSearch}
							onReset={handleResetFilters}
						/>

						<div className="card">
							<div className="card-header">
								<h5 className="mb-0">
									Inbound Reply History
									{pagination.total > 0 && (
										<span className="badge bg-secondary ms-2">
											{pagination.total.toLocaleString()} total
										</span>
									)}
								</h5>
							</div>
							<div className="card-body p-0">
								{isLoading ? (
									<div className="text-center py-5">
										<Spinner />
										<p className="text-muted mt-3">Loading replies...</p>
									</div>
								) : items.length === 0 && error ? (
									// Scoped to an EMPTY list on purpose: state.adminSmsResponses.error is written
									// by both the list thunk and fetchOne, so an unscoped read lets a failed detail
									// fetch delete a list that had loaded fine.
									<div className="p-3">{renderErrorAlert(error)}</div>
								) : items.length === 0 ? (
									<div className="text-center py-5">
										<i
											className="bi bi-inbox"
											style={{ fontSize: '3rem', opacity: 0.3 }}
										/>
										<p className="text-muted mt-3">No inbound SMS found</p>
										<button
											className="btn btn-sm btn-primary"
											onClick={handleResetFilters}
										>
											Clear Filters
										</button>
									</div>
								) : (
									<>
										<SmsResponseTable
											items={items}
											sortBy={filters.sort_by}
											sortOrder={filters.sort_order}
											onSort={handleSort}
											onViewDetails={handleViewDetails}
										/>
										{pagination.lastPage > 1 && (
											<div className="p-3 border-top">
												<Paginator
													totalPages={pagination.lastPage}
													currentPage={pagination.currentPage}
													lastPage={pagination.lastPage}
													setPage={handlePageChange}
												/>
												<div className="text-center text-muted mt-2">
													<small>
														Showing {pagination.from} to {pagination.to} of{' '}
														{pagination.total} replies
													</small>
												</div>
											</div>
										)}
									</>
								)}
							</div>
						</div>

						{showDetailsModal && selectedItem && (
							<SmsResponseDetailsModal
								item={selectedItem}
								onClose={() => {
									setShowDetailsModal(false);
									setSelectedItem(null);
								}}
							/>
						)}
					</>
				)}

				{/* OUTBOUND TAB */}
				{activeTab === 'outbound' && (
					<>
						{renderErrorAlert(outboundContextError)}

						<SmsOutboundStatistics
							statistics={outboundStatistics}
							isLoading={false}
							statsDays={outboundStatsDays}
							onDaysChange={setOutboundStatsDays}
							onRefresh={handleRefreshOutboundStats}
						/>

						<SmsOutboundFilters
							filters={outboundFilters}
							filterOptions={outboundFilterOptions}
							onFilterChange={handleOutboundFilterChange}
							onSearch={handleOutboundSearch}
							onReset={handleOutboundResetFilters}
						/>

						<div className="card">
							<div className="card-header">
								<h5 className="mb-0">
									Outbound Alert History
									{outboundPaginationData.total > 0 && (
										<span className="badge bg-secondary ms-2">
											{outboundPaginationData.total.toLocaleString()} total
										</span>
									)}
								</h5>
							</div>
							<div className="card-body p-0">
								{isLoadingOutboundList ? (
									<div className="text-center py-5">
										<Spinner />
										<p className="text-muted mt-3">Loading outbound alerts...</p>
									</div>
								) : outboundListError ? (
									<div className="p-3">
										{renderErrorAlert(outboundListError)}
									</div>
								) : outboundItemsList.length === 0 ? (
									<div className="text-center py-5">
										<i
											className="bi bi-send"
											style={{ fontSize: '3rem', opacity: 0.3 }}
										/>
										<p className="text-muted mt-3">No outbound SMS found</p>
										<button
											className="btn btn-sm btn-primary"
											onClick={handleOutboundResetFilters}
										>
											Clear Filters
										</button>
									</div>
								) : (
									<>
										<SmsOutboundTable
											items={outboundItemsList}
											sortBy={outboundFilters.sort_by}
											sortOrder={outboundFilters.sort_order}
											onSort={handleOutboundSort}
											onViewDetails={handleViewOutboundDetails}
										/>
										{outboundPaginationData.lastPage > 1 && (
											<div className="p-3 border-top">
												<Paginator
													totalPages={outboundPaginationData.lastPage}
													currentPage={outboundPaginationData.currentPage}
													lastPage={outboundPaginationData.lastPage}
													setPage={handleOutboundPageChange}
												/>
												<div className="text-center text-muted mt-2">
													<small>
														Showing {outboundPaginationData.from || 1} to{' '}
														{outboundPaginationData.to || outboundItemsList.length} of{' '}
														{outboundPaginationData.total} alerts
													</small>
												</div>
											</div>
										)}
									</>
								)}
							</div>
						</div>

						{showOutboundModal && selectedOutboundSms && (
							<SmsOutboundDetailsModal
								sms={selectedOutboundSms}
								onClose={() => {
									setShowOutboundModal(false);
									setSelectedOutboundSms(null);
								}}
							/>
						)}
					</>
				)}
			</div>
		</AdminLayout>
	);
});

export default AdminSmsResponses;
